import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Code,
  FileText,
  Globe,
  Home,
  Lock,
  Monitor,
  Palette,
  Rocket,
  Server,
  Settings,
  Shield,
  TestTube,
  Trophy,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Menu item interface
 */
export interface MenuItem {
  title: string;
  icon?: LucideIcon;
  path?: string;
  heading?: boolean;
  disabled?: boolean;
  children?: MenuItem[];
  badge?: string;
  separator?: boolean;
}

export type MenuConfig = MenuItem[];

/**
 * Tagna Admin Panel - Navigation Menu Configuration
 * Complete implementation of all 19 Epics from docs/src
 *
 * Epic Breakdown (87 routes total):
 * E1: Core Infrastructure (4 routes)
 * E2: Authentication & Authorization (6 routes)
 * E3: Dashboard & Analytics (3 routes)
 * E4: User Management (8 routes)
 * E5: Gamification Management (12 routes)
 * E6: Content Management (9 routes)
 * E7: Notifications Management (6 routes)
 * E8: Background Services (5 routes)
 * E9: Settings & Feature Flags (4 routes)
 * E10: Files & Media Management (6 routes)
 * E11: Accessibility & Theming (4 routes)
 * E12: Security & Compliance (5 routes)
 * E13: Observability & Telemetry (5 routes)
 * E14: Performance & Optimization (4 routes)
 * E15: Testing & QA Automation (6 routes)
 * E16: Dev Tooling & CI/CD (5 routes)
 * E17: Internationalization (7 routes)
 * E18: Advanced Analytics (6 routes)
 * E19: Hardening & Release Readiness (5 routes)
 */

export const ADMIN_MENU: MenuConfig = [
  // E3: Dashboard & Analytics
  {
    title: 'Dashboard',
    icon: Home,
    path: '/',
  },

  // Core Management Section
  { heading: true, title: 'Core Management' },

  // E4: User Management (8 routes)
  {
    title: 'User Management',
    icon: Users,
    children: [
      { title: 'All Users', path: '/users' },
      { title: 'User Lookup', path: '/users/lookup' },
      { title: 'Data Export', path: '/users/export' },
      { title: 'Roles & Permissions', path: '/users/roles' },
      { title: 'Activity Timeline', path: '/users/activity' },
      { title: 'Wallet Management', path: '/users/wallet' },
      { title: 'Bulk Operations', path: '/users/bulk' },
    ],
  },

  // E2: Authentication & Authorization (6 routes)
  {
    title: 'Auth & Permissions',
    icon: Lock,
    children: [
      { title: 'Permission Management', path: '/auth/permissions' },
      { title: 'Role Management', path: '/auth/roles' },
      { title: 'Access Control', path: '/auth/access-control' },
      { title: 'Session Management', path: '/auth/sessions' },
      { title: 'OAuth Providers', path: '/auth/oauth' },
      { title: 'JWT Configuration', path: '/auth/jwt-config' },
    ],
  },

  // E5: Gamification Management (12 routes)
  {
    title: 'Gamification',
    icon: Trophy,
    children: [
      { title: 'Wallet Management', path: '/gamification/wallets' },
      { title: 'Wallet Analytics', path: '/gamification/wallets/analytics' },
      { title: 'Wallet Statistics', path: '/gamification/wallets/statistics' },
      { title: 'Audit Log', path: '/gamification/wallets/audit-log' },
      { title: 'Reward Actions', path: '/gamification/reward-actions' },
      { title: 'Check-In Config', path: '/gamification/checkin/config' },
      { title: 'Check-In Analytics', path: '/gamification/checkin/analytics' },
      { title: 'Feedback', path: '/gamification/feedback' },
      { title: 'Prize Draws', path: '/gamification/rewards' },
      { title: 'Leaderboards', path: '/gamification/leaderboards' },
      { title: 'Referral System', path: '/gamification/referrals' },
      { title: 'Social Rewards', path: '/gamification/social-rewards' },
      { title: 'Achievements', path: '/gamification/achievements' },
      { title: 'Reward History', path: '/gamification/reward-history' },
    ],
  },

  // E6: Content Management (9 routes)
  {
    title: 'Content Management',
    icon: BookOpen,
    children: [
      { title: 'Content Dashboard', path: '/content/dashboard' },
      { title: 'Announcements', path: '/content/announcements' },
      { title: 'Email Templates', path: '/content/email-templates' },
      { title: 'Newsletter Campaigns', path: '/content/newsletters' },
      { title: 'Landing Pages', path: '/content/landing-pages' },
      { title: 'Blog Posts', path: '/content/blog' },
      { title: 'FAQs', path: '/content/faqs' },
      { title: 'Help Articles', path: '/content/help' },
      { title: 'Media Library', path: '/content/media' },
    ],
  },

  // E7: Notifications Management (6 routes)
  {
    title: 'Notifications',
    icon: Bell,
    children: [
      { title: 'Notification Center', path: '/notifications/center' },
      { title: 'Push Notifications', path: '/notifications/push' },
      { title: 'Email Campaigns', path: '/notifications/email' },
      { title: 'In-App Messages', path: '/notifications/in-app' },
      { title: 'SMS Notifications', path: '/notifications/sms' },
      { title: 'Templates', path: '/notifications/templates' },
    ],
  },

  // Analytics & Monitoring Section
  { heading: true, title: 'Analytics & Monitoring' },

  // E18: Advanced Analytics (6 routes)
  {
    title: 'Advanced Analytics',
    icon: BarChart3,
    children: [
      { title: 'BI Dashboard', path: '/analytics/bi-dashboard' },
      { title: 'User Analytics', path: '/analytics/users' },
      { title: 'Gamification Metrics', path: '/analytics/gamification' },
      { title: 'Revenue Reports', path: '/analytics/revenue' },
      { title: 'Custom Report Builder', path: '/analytics/custom' },
      { title: 'AI Insights', path: '/analytics/ai-insights' },
    ],
  },

  // E1: Core Infrastructure (4 routes)
  {
    title: 'System Health',
    icon: Monitor,
    children: [
      { title: 'Health Dashboard', path: '/infrastructure/health' },
      { title: 'Error Tracking', path: '/infrastructure/errors' },
      { title: 'API Monitoring', path: '/infrastructure/api' },
      { title: 'Performance Metrics', path: '/infrastructure/performance' },
    ],
  },

  // E13: Observability & Telemetry (5 routes)
  {
    title: 'Observability',
    icon: Activity,
    children: [
      { title: 'Real-time Events', path: '/observability/events' },
      { title: 'Log Viewer', path: '/observability/logs' },
      { title: 'Trace Explorer', path: '/observability/traces' },
      { title: 'Metrics Dashboard', path: '/observability/metrics' },
      { title: 'Alerting Rules', path: '/observability/alerts' },
    ],
  },

  // System Configuration Section
  { heading: true, title: 'System Configuration' },

  // E9: Settings & Feature Flags (4 routes)
  {
    title: 'Settings',
    icon: Settings,
    children: [
      { title: 'General Settings', path: '/settings/general' },
      { title: 'Feature Flags', path: '/settings/features' },
      { title: 'Email Configuration', path: '/settings/email' },
      { title: 'Payment Settings', path: '/settings/payment' },
    ],
  },

  // E8: Background Services (5 routes)
  {
    title: 'Background Services',
    icon: Server,
    children: [
      { title: 'Service Monitor', path: '/services/monitor' },
      { title: 'Job Queue', path: '/services/jobs' },
      { title: 'Scheduled Tasks', path: '/services/scheduled' },
      { title: 'Worker Status', path: '/services/workers' },
      { title: 'Service Logs', path: '/services/logs' },
    ],
  },

  // E10: Files & Media Management (6 routes)
  {
    title: 'Files & Media',
    icon: FileText,
    children: [
      { title: 'File Manager', path: '/files/manager' },
      { title: 'Upload Center', path: '/files/upload' },
      { title: 'Storage Analytics', path: '/files/analytics' },
      { title: 'CDN Management', path: '/files/cdn' },
      { title: 'Image Optimization', path: '/files/optimization' },
      { title: 'Cleanup Tools', path: '/files/cleanup' },
    ],
  },

  // E12: Security & Compliance (5 routes)
  {
    title: 'Security & Compliance',
    icon: Shield,
    children: [
      { title: 'Security Overview', path: '/security/overview' },
      { title: 'Audit Logs', path: '/security/audit-logs' },
      { title: 'API Keys', path: '/security/api-keys' },
      { title: 'Compliance Reports', path: '/security/compliance' },
      { title: 'Vulnerability Scanner', path: '/security/vulnerabilities' },
    ],
  },

  // Advanced Features Section
  { heading: true, title: 'Advanced Features' },

  // E17: Internationalization (7 routes)
  {
    title: 'Internationalization',
    icon: Globe,
    children: [
      { title: 'i18n Dashboard', path: '/i18n/dashboard' },
      { title: 'Language Management', path: '/i18n/languages' },
      { title: 'Translation Keys', path: '/i18n/keys' },
      { title: 'Translation Editor', path: '/i18n/editor' },
      { title: 'Quality Assurance', path: '/i18n/qa' },
      { title: 'Import/Export', path: '/i18n/import-export' },
      { title: 'i18n Settings', path: '/i18n/settings' },
    ],
  },

  // E11: Accessibility & Theming (4 routes)
  {
    title: 'Accessibility & Theming',
    icon: Palette,
    children: [
      { title: 'Theme Manager', path: '/theming/themes' },
      { title: 'WCAG Compliance', path: '/theming/wcag' },
      { title: 'Color Schemes', path: '/theming/colors' },
      { title: 'Accessibility Testing', path: '/theming/a11y-testing' },
    ],
  },

  // E14: Performance & Optimization (4 routes)
  {
    title: 'Performance',
    icon: Zap,
    children: [
      { title: 'Performance Dashboard', path: '/performance/dashboard' },
      { title: 'Bundle Analysis', path: '/performance/bundle' },
      { title: 'Core Web Vitals', path: '/performance/vitals' },
      { title: 'Optimization Tools', path: '/performance/optimization' },
    ],
  },

  // Development & Quality Section
  { heading: true, title: 'Development & Quality' },

  // E15: Testing & QA Automation (6 routes)
  {
    title: 'Testing & QA',
    icon: TestTube,
    children: [
      { title: 'Test Dashboard', path: '/testing/dashboard' },
      { title: 'Test Management', path: '/testing/management' },
      { title: 'Test Execution', path: '/testing/execution' },
      { title: 'Coverage Reports', path: '/testing/coverage' },
      { title: 'Quality Gates', path: '/testing/quality-gates' },
      { title: 'Test Automation', path: '/testing/automation' },
    ],
  },

  // E16: Dev Tooling & CI/CD (5 routes)
  {
    title: 'Development Tools',
    icon: Code,
    children: [
      { title: 'Dev Dashboard', path: '/dev/dashboard' },
      { title: 'CI/CD Pipelines', path: '/dev/pipelines' },
      { title: 'Deployment Manager', path: '/dev/deployment' },
      { title: 'Database Tools', path: '/dev/database' },
      { title: 'Git Integration', path: '/dev/git' },
    ],
  },

  // E19: Hardening & Release Readiness (5 routes)
  {
    title: 'Release Readiness',
    icon: Rocket,
    children: [
      { title: 'Readiness Dashboard', path: '/release/dashboard' },
      { title: 'Security Hardening', path: '/release/hardening' },
      { title: 'Production Validation', path: '/release/validation' },
      { title: 'Deployment Checklist', path: '/release/checklist' },
      { title: 'Release Notes', path: '/release/notes' },
    ],
  },
];
